<script lang="ts">
	import { getContext, onMount, type Component } from 'svelte';
	import { cubicOut } from 'svelte/easing';
	import { writable } from 'svelte/store';
	import { slide, type TransitionConfig } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';
	import { ChevronDownIcon } from '../../assets/icons/ChevronDownIcon';
	import { Txt, type TxtVariant } from '../Txt';
	import type { AccordionCtxType } from './Accordion.svelte';

	interface Props {
		dataCy: string;
		defaultOpen?: boolean;
		title?: string;
		titleSlot?: import('svelte').Snippet;
		titleVariant?: TxtVariant;
		classes?: {
			titleButton?: string;
			openTitleButton?: string;
			title?: string;
			icon?: string;
			contentWrapper?: string;
			content?: string;
		};
		open?: boolean;
		class?: string | undefined;
		onclick?: undefined | (() => void);
		children?: import('svelte').Snippet;
		Icon?: Component | undefined;
		contentTransition?: 'slide' | 'flexGrow';
	}

	let {
		dataCy,
		defaultOpen = false,
		title = '',
		titleVariant = 'h5' as TxtVariant,
		classes = {},
		open = $bindable(false),
		onclick = undefined,
		Icon = ChevronDownIcon,
		titleSlot,
		children,
		contentTransition = 'slide',
		...rest
	}: Props = $props();

	// When the content is a flex item of a fixed-height column, `slide` fights the
	// layout: it measures the element's height once at transition start, while the
	// flex-allotted height keeps changing as sibling sections animate — ending in a
	// snap. Animating the item's flex share instead lets flex redistribute the
	// space continuously each frame.
	const flexGrow = (
		_node: Element,
		params: { duration?: number; easing?: (t: number) => number }
	): TransitionConfig => ({
		duration: params.duration,
		easing: params.easing,
		css: (t) => `flex-grow: ${t}`
	});

	const contentTransitionFn = $derived(contentTransition === 'flexGrow' ? flexGrow : slide);

	let dataCyPrefix = $derived(`${dataCy}-accordion-item`);

	const ctx = getContext<AccordionCtxType>('accordion_ctx') ?? {};

	// single selection
	const self = {};
	const selected = ctx.selected ?? writable();

	onMount(() => {
		if (defaultOpen || open) {
			selected.set(self);
		}
	});

	$effect(() => {
		if ($selected === self) {
			open = true;
		} else {
			open = false;
		}
	});

	const handleToggle = () => {
		selected.set(open ? {} : self);

		if (onclick) {
			onclick();
		}
	};
</script>

<div {...rest}>
	<button
		onclick={handleToggle}
		type="button"
		data-cy={dataCyPrefix}
		class={twMerge([
			'flex w-full items-center justify-between',
			classes.titleButton,
			open ? classes.openTitleButton : ''
		])}
		aria-expanded={open}
		aria-label={`${open ? 'Close' : 'Open'} ${title ? `${title} ` : ''}accordion`}
	>
		{#if title}
			<Txt
				data-cy={`${dataCyPrefix}-title`}
				variant={titleVariant}
				class={twMerge(['py-4 text-left', classes.title])}
			>
				{title}
			</Txt>
		{:else}
			{@render titleSlot?.()}
		{/if}

		<span class:-rotate-180={open} class="flex transition-transform duration-300 ease-out">
			<Icon class={twMerge('h-[20px] w-[20px]', classes?.icon)} />
		</span>
	</button>

	<div class={classes?.contentWrapper}>
		{#if open}
			<div
				data-cy={`${dataCyPrefix}-content`}
				class={classes?.content}
				transition:contentTransitionFn={{ easing: cubicOut, duration: 300 }}
			>
				{@render children?.()}
			</div>
		{:else}
			<div class="hidden">
				{@render children?.()}
			</div>
		{/if}
	</div>
</div>
